import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import { listAreas, getAreaCapacita, deleteArea } from '../api/areas';
import type { Area, AreaCapacita } from '../api/areas';
import AreaModal from '../components/modals/AreaModal';
import { useAuthStore } from '../store/authStore';
import { canWriteSettings } from '../lib/constants';
import { fmtKg } from '../lib/saldo';


// F2 (V48): lo stesso codice puo' coprire piu' materiali (righe) — al hover
// mostra tutte le descrizioni dei materiali ammessi con quel codice.
function descrizioniPerCer(area: Area, cer: string, sep = '\n'): string {
  const descrizioni = area.materiali_ammessi
    .filter((m) => m.cer === cer)
    .map((m) => m.nome_descrittivo)
    .filter((d): d is string => !!d);
  return descrizioni.length > 0 ? descrizioni.join(sep) : cer;
}

function CapacitaBar({ pct }: { pct: number }) {
  const cls = pct > 80 ? 'bg-red-500' : pct > 50 ? 'bg-amber-400' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-stone-bg rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${cls}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <span className="text-xs text-ink-soft w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

export default function AreePage() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  // Scrittura aree (crea/modifica/disattiva) = {admin, DIR, RT}; AMM sola lettura.
  const canWrite = canWriteSettings(user?.role);

  const [areas, setAreas] = useState<Area[]>([]);
  const [capacita, setCapacita] = useState<Record<string, AreaCapacita>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editArea, setEditArea] = useState<Area | null | 'new'>(null);
  // Default: solo aree attive; la checkbox include anche le disattivate.
  const [includiDisattivate, setIncludiDisattivate] = useState(false);

  const loadAreas = useCallback(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await listAreas(includiDisattivate);
        setAreas(data);
        const capPromises = data.map((a) =>
          getAreaCapacita(a.id).then((c) => [a.id, c] as [string, AreaCapacita]).catch(() => null)
        );
        const caps = await Promise.all(capPromises);
        const capMap: Record<string, AreaCapacita> = {};
        caps.forEach((c) => { if (c) capMap[c[0]] = c[1]; });
        setCapacita(capMap);
      } catch {
        setError(t('aree.errore_caricamento'));
      } finally {
        setLoading(false);
      }
    })();
  }, [includiDisattivate, t]);

  useEffect(() => { loadAreas(); }, [loadAreas]);

  async function handleDelete(id: string) {
    if (!confirm(t('aree.conferma_disattiva'))) return;
    try {
      await deleteArea(id);
      loadAreas();
    } catch {
      alert(t('aree.errore_elimina'));
    }
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-ink-dark">{t('aree.titolo')}</h1>
          <p className="text-sm text-ink-soft mt-0.5">{t('aree.conteggio', { n: areas.length })}</p>
        </div>
        <div className="flex items-center gap-2">
          {canWrite && (
            <label className="flex items-center gap-1.5 text-sm text-ink-mid cursor-pointer select-none mr-1">
              <input type="checkbox" className="w-4 h-4 rounded border-stone-border"
                checked={includiDisattivate} onChange={(e) => setIncludiDisattivate(e.target.checked)} />
              {t('aree.mostra_disattivate')}
            </label>
          )}
          <button type="button" onClick={loadAreas} className="p-2 text-ink-soft hover:text-ink-dark border border-stone-border rounded-lg bg-white" title={t('aree.aggiorna')}>
            <RefreshCw size={15} />
          </button>
          {canWrite && (
            <button
              type="button"
              onClick={() => setEditArea('new')}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors"
            >
              <Plus size={15} /> {t('aree.nuova_area')}
            </button>
          )}
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-accent-danger-bg border border-[#FECACA] rounded-lg text-sm text-accent-danger">{error}</div>}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-ink-soft text-sm">{t('aree.caricamento')}</div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-stone-mid">
          <table className="w-full text-sm">
            <thead className="bg-stone-bg border-b border-stone-mid">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_codice')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_descrizione')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_categoria')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_capacita')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide w-40">{t('aree.col_utilizzo')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_cer')}</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_stato')}</th>
                {canWrite && <th className="px-4 py-3 text-right text-xs font-semibold text-ink-soft uppercase tracking-wide">{t('aree.col_azioni')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-mid">
              {areas.length === 0 && (
                <tr><td colSpan={canWrite ? 8 : 7} className="px-4 py-8 text-center text-sm text-ink-faint">{t('aree.vuoto')}</td></tr>
              )}
              {areas.map((a) => {
                const cap = capacita[a.id];
                const pct = cap ? cap.percentuale_utilizzo : 0;
                return (
                  <tr key={a.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3 font-mono font-semibold text-ink-dark">{a.codice}</td>
                    <td className="px-4 py-3 text-ink-dark">{a.descrizione}</td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-medium text-ink-mid">{a.categoria}</span>
                    </td>
                    <td className="px-4 py-3 text-ink-mid text-xs whitespace-nowrap">
                      {cap ? `${fmtKg(cap.peso_attuale_kg)} / ${fmtKg(a.capacita_kg)} kg` : `${fmtKg(a.capacita_kg)} kg`}
                    </td>
                    <td className="px-4 py-3">
                      <CapacitaBar pct={pct} />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {a.cer_ammessi.slice(0, 3).map((cer) => (
                          <span key={cer} title={descrizioniPerCer(a, cer)} className="text-xs bg-stone-bg text-ink-mid px-1.5 py-0.5 rounded font-mono cursor-default">{cer}</span>
                        ))}
                        {a.cer_ammessi.length > 3 && (
                          <span title={a.cer_ammessi.slice(3).map((cer) => `${cer} — ${descrizioniPerCer(a, cer, ' · ')}`).join('\n')} className="text-xs text-ink-faint cursor-default">+{a.cer_ammessi.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${a.attiva ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-ink-soft'}`}>
                        {a.attiva ? t('aree.stato_attiva') : t('aree.stato_disattiva')}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button type="button" onClick={() => setEditArea(a)} className="p-1.5 text-ink-soft hover:text-sage rounded transition-colors" title={t('aree.modifica')}>
                            <Pencil size={14} />
                          </button>
                          <button type="button" onClick={() => handleDelete(a.id)} className="p-1.5 text-ink-soft hover:text-accent-danger rounded transition-colors" title={t('aree.disattiva')}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editArea !== null && (
        <AreaModal
          area={typeof editArea === 'string' ? undefined : editArea}
          onClose={() => setEditArea(null)}
          onSaved={() => { setEditArea(null); loadAreas(); }}
        />
      )}
    </div>
  );
}
